import { useSelector, useDispatch } from 'react-redux';
import { getDataLinkPath } from '../../../page/utils';
import { Timestamp } from 'firebase/firestore';

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  getFirestore
} from 'firebase/firestore';
import useDataLinkDatabase from '../../../database/datalink';
import { getFormValues, getStepFieldValue } from '../utils';
import CryptoJS from 'crypto-js';
import { keyUpdatedAction } from '../../../../redux/actions/local';
import useCloud from '../../../cloud';
import useParse from '../../../parse';
import { dataLinkDocumentsAppendedAction } from '../../../../redux/actions/datalinks';
import { isNumber } from 'lodash';

const useSearch = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.User);
  const { dataLinks } = useSelector(state => state.DataLinks);
  const local = useSelector(state => state.Local);
  const { callFunction } = useCloud();
  const { parse } = useParse();

  const { getDb } = useDataLinkDatabase();

  const modifySearchData = async ({ results, code }) => {
    try {
      if (code?.trim().length > 0) {
        // eslint-disable-next-line
        const AsyncFunction = Object.getPrototypeOf(async function() {})
          .constructor;

        const modifyDataFunction = new AsyncFunction(
          'results',
          'countDocuments',
          'user',
          'local',
          code
        );

        const countDocuments = async ({
          databaseId = '',
          path,
          filters = []
        }) => {
          let countQuery;

          const queryParts = filters.map(filter => {
            return where(filter.field, filter.operator, filter.value);
          });

          const countDb = getFirestore(databaseId);

          if (queryParts.length > 0) {
            countQuery = query(collection(countDb, path), ...queryParts);
          } else {
            countQuery = query(collection(countDb, path));
          }

          const itemCountSnap = await getCountFromServer(countQuery);
          return itemCountSnap.data().count;
        };

        const data = await modifyDataFunction(
          results,
          countDocuments,
          user,
          local
        );
        return data;
      }
    } catch (error) {
      console.error('modifySearchData could not be executed', error);
      return results;
    }
  };

  /*
    Searches for data directly on firestore against a dataLink, honoring firestore security rules
    If the field to search on is encrypted, we search against the hashed value.
    The search gets it input from the local store under a search key:

    It can also get its values from a formId if supplied.
    Values are loaded from local first and overwritten by anything in a matching formId

    search: {
        formId,
        dataLinkId,
        field, (optional) <-- if omitted, the first page of results will be loaded for all documents in the datalink
        value, (optional)
        page
    }

    Search is managed within the local store. The contents of the local store are held under key called "search"
      or keyed by step.searchLocalStoreKeyName

    {
        search: {
            field,
            value,
            sortField, (optional) by default sorts by field
            pageCount, // total number of pages of records
            pageNumber, // current page index we are viewing
            count, // total number of matched records
            pages_1,
            pages_2: {
                results: [<the matching documents for this page>],
                count: the number of documents on this page
            }
            pages_3 etc
        }
    }

  */
  const search = async ({
    page,
    sequence,
    step,
    documents,
    selections,
    local,
    data,
    params,
    history
  }) => {
    let inputs = {};

    const searchName =
      step.searchLocalStoreKeyName?.trim().length > 0
        ? step.searchLocalStoreKeyName.trim()
        : 'search';

    let payload = {
      ...(local[searchName] || {})
    };

    if (step.formId?.trim().length > 0) {
      inputs = getFormValues({
        formId: step.formId,
        returnObject: true
      });

      if (
        inputs !== undefined &&
        Object.keys(inputs).length > 0 &&
        step.formId?.trim().length > 0
      ) {
        payload = { ...inputs };
      }
    }

    let additionalSearchFields = [];

    step.fields?.forEach(stepField => {
      const stepFieldValue = getStepFieldValue({
        stepField,
        document,
        history,
        selections,
        documents,
        user,
        local,
        data: {
          ...data,
          ...inputs
        },
        dataLinks,
        params
      });
      if (stepField.dynamicFieldName?.trim().length > 0) {
        const targetField = parse({
          local,
          markup: stepField.dynamicFieldName,
          actionSequenceExecutionHistory: history,
          data: {
            ...data,
            ...inputs
          },
          params
        });
        if (['sortField', 'sortDirection'].some(f => f === targetField)) {
          payload[targetField] = stepFieldValue;
        } else {
          additionalSearchFields.push(
            where(
              stepField.dynamicFieldName,
              stepField.operator,
              stepFieldValue
            )
          );
        }
      } else {
        if (['sortField', 'sortDirection'].some(f => f === stepField.name)) {
          payload[stepField.name] = stepFieldValue;
        } else {
          additionalSearchFields.push(
            where(stepField.name, stepField.operator, stepFieldValue)
          );
        }
      }
    });

    const pageDataLink = page.dataLinks.find(
      d => d.dataLinkId === step.searchDataLinkId
    );

    if (!pageDataLink) {
      return {
        error: `sequence "${sequence.name}", step: ${step.name} cannot be processed because the data link with id ${step.searchDataLinkId} set against the step cannot be found.`
      };
    }

    const path = getDataLinkPath({
      dataLink: pageDataLink.dataLink,
      documents,
      selections,
      user,
      local,
      dataLinks,
      data,
      params
    });

    const db = getDb(pageDataLink.dataLinkId);

    let field;

    if (payload.field) {
      additionalSearchFields = []; // additional search fields are only supported when viewing all records, otherwise composite indexes have to be created when searching and paginating on multiple fields
      field = pageDataLink.dataLink.contentType.fields.find(
        f => f.name === payload.field
      );
      if (!field) {
        // dynamic field
        field = {
          name: payload.field,
          encrypted: false
        };
      }
    }

    /*if (!field || !payload.value) {
      return {
        error: `sequence "${sequence.name}", step: ${step.name} cannot be processed because the either search field or search value were not supplied.`
      };
    }*/

    let searchValue = field?.encrypted
      ? CryptoJS.SHA256(payload.value).toString(CryptoJS.enc.Hex)
      : payload.value;

    let fieldName = field?.encrypted ? `${field?.name}_hash` : field?.name;
    let pageSize = payload.pageSize || step.searchPageSize || 100;

    try {
      if (
        searchValue &&
        !isNumber(searchValue) &&
        searchValue !== null &&
        searchValue.toLowerCase
      ) {
        if (searchValue.toLowerCase === 'true') {
          searchValue = true;
        } else if (searchValue.toLowerCase === 'false') {
          searchValue = false;
        } else if (step.searchCasing === 'uppercase') {
          searchValue = searchValue.toLowerCase();
        } else if (step.searchCasing === 'lowercase') {
          searchValue = searchValue.toLowerCase();
        } else if (step.searchCasing === 'propercase') {
          searchValue = searchValue
            .split(' ')
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ');
        }
      }
    } catch {
      // na
    }

    if (searchValue?.toString().toLowerCase() === 'true') {
      searchValue = true;
    } else if (searchValue?.toString().toLowerCase() === 'false') {
      searchValue = false;
    }

    /*console.log({
      payload,
      searchPageSize: step.searchPageSize,
      fieldName,
      searchValue
    });*/

    let queryParts = [];

    if (fieldName) {
      if (step.searchExcludeDeleted) {
        queryParts = [
          where(fieldName, '==', searchValue),
          where('deleted', '!=', true),
          ...additionalSearchFields
        ];
      } else {
        queryParts = [
          where(fieldName, '==', searchValue),
          ...additionalSearchFields
        ];
      }
    } else if (step.searchExcludeDeleted) {
      queryParts = [where('deleted', '!=', true), ...additionalSearchFields];
    } else {
      queryParts = [...additionalSearchFields];
    }

    if (!payload.count) {
      let pageCountSnap;

      if (fieldName) {
        pageCountSnap = await getCountFromServer(
          step.searchExcludeDeleted
            ? query(
                collection(db, path),
                where(fieldName, '==', searchValue),
                where('deleted', '!=', true),
                ...additionalSearchFields
              )
            : query(
                collection(db, path),
                where(fieldName, '==', searchValue),
                ...additionalSearchFields
              )
        );
      } else {
        pageCountSnap = await getCountFromServer(
          step.searchExcludeDeleted
            ? query(
                collection(db, path),
                where('deleted', '!=', true),
                ...additionalSearchFields
              )
            : query(collection(db, path), ...additionalSearchFields)
        );
      }
      const totalDocuments = pageCountSnap.data().count;
      payload.pageCount = Math.ceil(totalDocuments / pageSize);
      payload.count = totalDocuments;
    }

    // If it's not the first page, set the cursor to the last visible document from the previous page
    if (payload.pageNumber > 1 && payload.lastVisible) {
      if (fieldName) {
        if (step.searchExcludeDeleted) {
          queryParts = [
            where(fieldName, '==', searchValue),
            where('deleted', '!=', true),
            ...additionalSearchFields,
            startAfter(payload.lastVisible)
          ];
        } else {
          queryParts = [
            where(fieldName, '==', searchValue),
            ...additionalSearchFields,
            startAfter(payload.lastVisible)
          ];
        }
      } else if (step.searchExcludeDeleted) {
        queryParts = [
          where('deleted', '!=', true),
          ...additionalSearchFields,
          startAfter(payload.lastVisible)
        ];
      } else {
        queryParts = [
          ...additionalSearchFields,
          startAfter(payload.lastVisible)
        ];
      }
    }

    if (step.searchSortOnServer && payload.sortField) {
      queryParts.push(
        orderBy(
          payload.sortField,
          payload.sortDirection ? payload.sortDirection : 'asc'
        )
      );
    }
    queryParts.push(limit(pageSize));

    // Fetch the documents for the requested page
    let snapshot;
    try {
      snapshot = await getDocs(query(collection(db, path), ...queryParts));
    } catch (err) {
      dispatch(keyUpdatedAction(`${searchName}_error`, err.toString()));
      return { error: err };
    }

    const lastVisible = snapshot.docs[snapshot.docs.length - 1]; // Update the last visible document

    let results = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    let requiresDecryption = false;

    results.forEach(result => {
      Object.keys(result).forEach(f => {
        if (
          result[f] &&
          result[f].indexOf &&
          result[f].indexOf('__ENC[') > -1
        ) {
          requiresDecryption = true;
        }
      });
    });

    if (requiresDecryption && Array.isArray(results) && results.length > 0) {
      const decryptedResults = await callFunction('data_decrypt_documents', {
        preserve: false,
        documents: results,
        contentTypeName: pageDataLink.dataLink.contentType.type
      });
      results = decryptedResults.data.documents;
    }

    if (!payload.pages || !payload.pageNumber) {
      payload.pages = {};
    }

    if (!payload.pageNumber) {
      payload.pageNumber = 1;
    }

    if (!step.searchSortOnServer) {
      results = [
        ...results.sort((a, b) => {
          const field = payload.sortField || payload.field;

          let valueA = a[field];
          let valueB = b[field];

          if (valueA?.seconds) {
            const ts = new Timestamp(valueA.seconds, valueA.nanoseconds);
            valueA = ts.toDate();
          }

          if (valueB?.seconds) {
            const ts = new Timestamp(valueB.seconds, valueB.nanoseconds);
            valueB = ts.toDate();
          }

          if (valueA === undefined || valueA === null) return -1;
          if (valueB === undefined || valueB === null) return 1;

          if (valueA === valueB) return 0;

          // Type-safe comparison for strings and numbers
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            if (payload.sortDirection === 'asc') {
              return valueA.localeCompare(valueB);
            }
            return valueB.localeCompare(valueA);
          }

          if (payload.sortDirection === 'asc') {
            return valueA > valueB ? 1 : -1;
          }

          return valueA < valueB ? 1 : -1;
        })
      ];
    }

    if (step.searchFunctionCode?.trim().length > 0) {
      results = await modifySearchData({
        results,
        code: step.searchFunctionCode
      });
    }

    payload.pages[`page_${payload.pageNumber || 1}`] = {
      results,
      count: results.length
    };

    payload = {
      ...payload,
      lastVisible,
      page: payload.pages[`page_${payload.pageNumber}`]
    };

    dispatch(keyUpdatedAction(searchName, payload));

    // If this is a search, then its possible that the underlying data link has not loaded this document based on its pageSize
    const dataLinkDocuments = documents[pageDataLink.dataLinkId];

    const newItems = [
      ...results.filter(
        r => !dataLinkDocuments.documents.find(d => d.id === r.id)
      )
    ];
    if (newItems.length > 0) {
      dispatch(
        dataLinkDocumentsAppendedAction({
          documents: newItems,
          dataLinkId: pageDataLink.dataLinkId
        })
      );
    }

    return {
      success: `${results.length} ${pageDataLink.dataLinkNamePlural} matched.`
    };
  };

  /*
    Goes to the previous page in the search results.
  */
  const previousPage = async payload => {
    const { local, step } = payload;

    if (!local || !step) {
      return;
    }

    if (local?.pageNumber === 1) {
      // We are already on the first page
      return;
    }

    const searchName =
      step.searchLocalStoreKeyName?.trim().length > 0
        ? step.searchLocalStoreKeyName.trim()
        : 'search';

    const newPayload = {
      ...payload
    };

    newPayload.local[searchName] = {
      ...(payload.local[searchName] || {}),
      pageNumber: parseInt(payload.local[searchName].pageNumber) - 1
    };

    if (
      local[searchName]?.pages[`page_${local[searchName]?.pageNumber - 1}`] !==
      undefined
    ) {
      newPayload.local[searchName].page =
        local[searchName].pages[`page_${local[searchName]?.pageNumber - 1}`];
      dispatch(keyUpdatedAction(searchName, newPayload.local[searchName]));
    }

    await search(newPayload);
  };

  /*
    Goes to the next page in the search results.
  */
  const nextPage = async payload => {
    const { local, step } = payload;

    if (!local || !step) {
      return;
    }

    const searchName =
      step.searchLocalStoreKeyName?.trim().length > 0
        ? step.searchLocalStoreKeyName.trim()
        : 'search';

    if (local[searchName]?.pageNumber === local[searchName].pageCount) {
      // We are already on the last page
      return;
    }

    const newPayload = {
      ...payload
    };

    newPayload.local[searchName] = {
      ...(payload.local[searchName] || {}),
      pageNumber: parseInt(payload.local[searchName].pageNumber) + 1
    };

    if (
      local[searchName].pages[`page_${local[searchName].pageNumber + 1}`] !==
      undefined
    ) {
      newPayload.local[searchName].page =
        local[searchName].pages[`page_${local[searchName].pageNumber + 1}`];
      dispatch(keyUpdatedAction(searchName, newPayload.local[searchName]));
      return;
    }
    await search(newPayload);
  };

  return {
    search,
    previousPage,
    nextPage
  };
};

export default useSearch;
